<template>
  <v-row v-if="loading">
    <v-col cols="12">
      <v-skeleton-loader
        type="card-heading, table-tbody, table-tbody, table-tfoot"
      />
    </v-col>
  </v-row>
  <v-row v-else-if="mobile" v-show="permissions.data.length">
    <template v-for="(perms, label) in permissionGroups" :key="label">
      <v-col cols="12">
        <v-card-title class="py-0 px-0">
          {{ label }}
        </v-card-title>
        <div v-for="permission in perms" :key="permission.id" class="pl-2">
          <checkbox-permission
            :dense="false"
            v-model="selectedPermissions"
            :permission="permission"
            :is-required="requiredIds.has(permission.id)"
            :disabled="disabled"
          />
        </div>
        <v-divider class="mt-4" />
      </v-col>
    </template>
  </v-row>
  <v-row v-else>
    <v-col cols="12">
      <v-card-title class="ml-0 pl-0 pt-2">Permissões</v-card-title>
      <v-divider class="mb-2" />
    </v-col>
    <v-col cols="12">
      <v-row v-for="(perms, label) in permissionGroups" :key="label">
        <v-col cols="2">
          {{ label }}
        </v-col>
        <v-col :cols="perms.length <= 4 ? '10' : '8'" v-if="!loading">
          <v-row no-gutters>
            <v-col
              v-for="(permission, i) in perms"
              :key="i"
              class="pl-4"
              :cols="perms.length <= 4 ? 'auto' : '4'"
            >
              <checkbox-permission
                v-model="selectedPermissions"
                :permission="permission"
                :is-required="requiredIds.has(permission.id)"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-spacer />

        <v-col cols="12" class="my-0 py-0">
          <v-divider />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import { useDisplay } from "vuetify";
import CheckboxPermission from "./CheckboxPermission.vue";
import { usePermissionsQuery } from "@/queries/modules/permissions";
import { useSnackbar } from "@/plugins/snackbar";

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  roleType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const { mobile } = useDisplay();
const snackbar = useSnackbar();

const requiredIds = ref(new Set());
const permissionMap = ref(new Map());
const permissionGroups = reactive({});
const selectedPermissions = ref([...props.modelValue]);

// TanStack Query for permissions
const permissionParams = computed(() => {
  if (props.roleType === "secondary") {
    return { type: "default" };
  }
  return {};
});
const permissionsQuery = usePermissionsQuery(permissionParams);
const loading = computed(() => permissionsQuery.isLoading.value);
const permissions = computed(() => ({
  loading: permissionsQuery.isLoading.value,
  data: permissionsQuery.data.value?.items || [],
}));

const order = [
  "read",
  "create",
  "create_type_nfce",
  "update",
  "delete",
  "set_csc",
  "read_csc",
  "set_certificate",
  "read_certificate",
];

// Process permissions when data loads
watch(
  () => permissionsQuery.data.value?.items,
  (newData) => {
    if (!newData) return;

    // Clear previous data
    permissionMap.value.clear();
    Object.keys(permissionGroups).forEach(
      (key) => delete permissionGroups[key],
    );

    for (const permission of newData) {
      permissionMap.value.set(permission.id, permission);
      if (!permissionGroups[permission.resource_description]) {
        permissionGroups[permission.resource_description] = [];
      }
      permissionGroups[permission.resource_description].push(permission);

      permissionGroups[permission.resource_description].sort((a, b) => {
        return order.indexOf(a.action) - order.indexOf(b.action);
      });
    }

    updateDependencies();
  },
  { immediate: true },
);

const updateDependencies = () => {
  const dependencies = selectedPermissions.value
    .map((pId) => permissionMap.value.get(pId))
    .filter((permission) => !!permission)
    .flatMap((permission) => permission.depends_on || []);

  requiredIds.value = new Set();

  for (const dependency of dependencies) {
    for (const permissionId of checkPermission(dependency)) {
      requiredIds.value.add(permissionId);
    }
  }
};

function* checkPermission(permission) {
  permission = permissionMap.value.get(permission.id);
  if (permission.depends_on) {
    for (const dependency of permission.depends_on) {
      yield* checkPermission(dependency);
    }
  }
  if (!selectedPermissions.value.includes(permission.id)) {
    selectedPermissions.value.push(permission.id);
  }
  yield permission.id;
}

watch(
  selectedPermissions,
  (permissions, old) => {
    updateDependencies();
    if (permissions !== old) {
      emit("update:modelValue", selectedPermissions.value);
    }
  },
  { deep: true },
);

watch(
  () => props.modelValue,
  (newVal) => {
    selectedPermissions.value = [...newVal];
    updateDependencies();
  },
  { immediate: true },
);
</script>
